import apiClient from '@/utils/httpClient';
import { CashflowKPIs, BusinessOverviewKPIs, ReceivablesKPIs, PayablesKPIs, AgedReceivablesKPIs, AgedPayablesKPIs } from '@blinkbooks/types';

export interface KpiDateRange {
  startDate: string;
  endDate: string;
}

/**
 * Fetch cash health KPIs: cash balance, net cash flow, burn rate, runway,
 * operating cash flow, and non-operating cash flow.
 * All derived values (burn rate, runway, non-op CF) are computed server-side.
 */
export function fetchCashflowKPIs(range: KpiDateRange): Promise<CashflowKPIs> {
  return apiClient.get<CashflowKPIs>('/kpi/cashflow', range);
}

/**
 * Fetch operating performance KPIs: cash collected, cash expenses, net profit,
 * avg days to collect, revenue YTD, EBITDA, gross margin.
 */
export function fetchBusinessKPIs(range: KpiDateRange, customers?: string[]): Promise<BusinessOverviewKPIs> {
  const params: Record<string, string | number | boolean> = { ...range };
  if (customers && customers.length > 0) params.customer = customers.join('|');
  return apiClient.get<BusinessOverviewKPIs>('/kpi/business-overview', params);
}

/**
 * Fetch receivables KPIs: total billed, collected, current, at-risk, overdue.
 * All buckets are scoped to the supplied date range.
 */
export function fetchReceivablesKPIs(range: KpiDateRange, customers?: string[]): Promise<ReceivablesKPIs> {
  const params: Record<string, string | number | boolean> = { ...range };
  if (customers && customers.length > 0) params.customer = customers.join('|');
  return apiClient.get<ReceivablesKPIs>('/kpi/receivables', params);
}

/**
 * Fetch Accounts Receivable aging from QBO AgedReceivableDetail report.
 * Returns total AR, current, at-risk (1–30d), and overdue (31+d) as of the period end date.
 */
export function fetchAgedReceivablesKPIs(range: KpiDateRange, customers?: string[]): Promise<AgedReceivablesKPIs> {
  const params: Record<string, string | number | boolean> = { ...range };
  if (customers && customers.length > 0) params.customer = customers.join('|');
  return apiClient.get<AgedReceivablesKPIs>('/kpi/aged-receivables', params);
}

export interface CashInflowRow {
  id: string;
  transactionDate: string;
  amount: number;
  merchantName: string;
  description: string;
  category: string | null;
  bankAccountName: string;
}

/**
 * Fetch individual bank inflow transactions used for the Cash Inflows KPI.
 * direction=IN, excludes clearing accounts, transfers, loans, financing, owner injections.
 */
export function fetchCashInflowDetails(range: KpiDateRange): Promise<CashInflowRow[]> {
  return apiClient.get('/kpi/cash-inflows', range);
}

export interface CashOutflowRow {
  id: string;
  transactionDate: string;
  amount: number;
  merchantName: string;
  description: string;
  category: string | null;
  bankAccountName: string;
}

/**
 * Fetch individual bank outflow transactions used for the Cash Outflows KPI.
 * direction=OUT (amounts returned as positive), excludes clearing accounts,
 * transfers, debt/loan repayments, and investments.
 */
export function fetchCashOutflowDetails(range: KpiDateRange): Promise<CashOutflowRow[]> {
  return apiClient.get('/kpi/cash-outflows', range);
}

/**
 * Fetch payables KPIs: total, unpaid, overdue buckets, paid, highest item, top category.
 * All buckets are scoped to the supplied date range and optional filters.
 */
export function fetchPayablesKPIs(
  range: KpiDateRange,
  vendors?: string[],
  sourceTypes?: string[],
): Promise<PayablesKPIs> {
  const params: Record<string, string | number | boolean> = { ...range };
  if (vendors && vendors.length > 0) params.vendor = vendors.join(',');
  if (sourceTypes && sourceTypes.length > 0) params.sourceType = sourceTypes.join(',');
  return apiClient.get<PayablesKPIs>('/kpi/payables', params);
}

/**
 * Fetch Accounts Payable aging from QBO AgedPayableDetail report.
 * Returns total AP, current, at-risk (1–30d), and overdue (31+d) as of the period end date.
 */
export function fetchAgedPayablesKPIs(range: KpiDateRange, vendors?: string[]): Promise<AgedPayablesKPIs> {
  const params: Record<string, string | number | boolean> = { ...range };
  if (vendors && vendors.length > 0) params.vendor = vendors.join(',');
  return apiClient.get<AgedPayablesKPIs>('/kpi/aged-payables', params);
}

export interface FinancialStructureSummary {
  totalCash: number;
  customerReceivables: number;
  vendorPayables: number;
}

export function fetchFinancialStructureSummary(): Promise<FinancialStructureSummary> {
  return apiClient.get<FinancialStructureSummary>('/kpi/financial-structure-summary');
}
